import {
  type FsmDefFormState,
  type FsmDefFormSuccess,
  type FsmDefFormSuccessContent,
} from "@do/shared-types";
import { Button } from "@do/ui/components/button";
import { Checkbox } from "@do/ui/components/checkbox";
import { Field, FieldError } from "@do/ui/components/field";
import { Input, type InputProps } from "@do/ui/components/input";
import { Label } from "@do/ui/components/label";
import { Progress } from "@do/ui/components/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type SelectTriggerProps,
} from "@do/ui/components/select";
import { Textarea, type TextareaProps } from "@do/ui/components/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@do/ui/components/tooltip";
import { cn } from "@do/ui/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { InfoIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { type AnyEventObject } from "xstate";
import { z } from "zod";
import {
  MarkdownRenderer,
  type MarkdownRendererProps,
} from "../../../../components/markdown-renderer";
import { escapeValue } from "../../../../lib/escape-value";
import { useMotionEnabled } from "../../../../hooks/use-motion-enabled";
import { INSTANT_TRANSITION } from "../../../../lib/motion-presets";
import { useChat } from "../../../../providers/chat-provider";
import { type StepValues } from "../../../../types/global";
import { ButtonIcon } from "../buttons/button-icon";
import { getFormSuccessTrigger, resolveFormSuccessContent } from "./form-success";
import { buildZodFromRules, type ValidationRule } from "./form-validation";

enum FieldType {
  markdown = "markdown",
  input = "input",
  textarea = "textarea",
  select = "select",
  textfield = "textfield",
  checkbox = "checkbox",
}

type FieldMarkdown = {
  type: FieldType.markdown;
  props: MarkdownRendererProps;
};

type FieldTextFieldInputProps = InputProps & {
  label: string;
  description?: string;
  tooltip?: string;
  multiline?: false;
  inputProps?: InputProps;
};

type FieldTextFieldTextareaProps = TextareaProps & {
  label: string;
  description?: string;
  tooltip?: string;
  multiline: true;
  minRows?: number;
};

type FieldTextField = {
  type: FieldType.textfield;
  props: FieldTextFieldInputProps | FieldTextFieldTextareaProps;
  defaultValue?: string | null;
  validationRules?: ValidationRule[];
};

type FieldInput = {
  type: FieldType.input;
  props: InputProps & {
    label: string;
    description?: string;
    tooltip?: string;
    inputProps?: InputProps;
  };
  defaultValue?: string | null;
  validationRules?: ValidationRule[];
};

type FieldTextArea = {
  type: FieldType.textarea;
  props: TextareaProps & {
    label: string;
    description?: string;
    tooltip?: string;
  };
  defaultValue?: string | null;
  validationRules?: ValidationRule[];
};

type FieldSelect = {
  type: FieldType.select;
  props: SelectTriggerProps & {
    label: string;
    description?: string;
    tooltip?: string;
    placeholder: string;
    required?: boolean;
    options?: { label: string; value: string }[];
  };
  defaultValue?: string | null;
  validationRules?: ValidationRule[];
};

type FieldCheckbox = {
  type: FieldType.checkbox;
  props: {
    label: string;
    description?: string;
    tooltip?: string;
    required?: boolean;
  };
  defaultValue?: boolean | null;
  validationRules?: ValidationRule[];
};

type FieldDef =
  | FieldMarkdown
  | FieldTextField
  | FieldTextArea
  | FieldInput
  | FieldSelect
  | FieldCheckbox;

type FormValues = Record<string, string | boolean>;

const PAGE_TRANSITION_DURATION_SECONDS = 0.3;
const PAGE_HEIGHT_TRANSITION_DURATION_SECONDS = 0.4;

type FormPage = {
  id: string;
  fields: string[];
  header?: LocalizedFormHeader;
};

type FormPageDef = Omit<FormPage, "id">;

type FormHeader = {
  title?: string;
  subtitle?: string;
};

type LocalizedFormHeader = Record<string, FormHeader>;

type FormSchema = {
  fields: Record<string, FieldDef>;
  header?: LocalizedFormHeader;
  pages?: Record<string, FormPageDef>;
  submit: string;
  success?: FsmDefFormSuccess;
};

const hasRequiredValidationRule = (validationRules?: ValidationRule[]) =>
  validationRules?.some((rule) => rule.rule === "required") ?? false;

const isFieldRequired = (field: Exclude<FieldDef, FieldMarkdown>) =>
  hasRequiredValidationRule(field.validationRules);

const labelClassName = cn(
  "leading-[1]!",
  "text-(length:--message-steps-form-label-font-size)",
  "font-(--message-steps-form-label-font-weight)!",
  "font-(family-name:--message-steps-form-label-font-family)",
  "text-(--message-steps-form-label-color)/(--message-steps-form-label-color-opacity)"
);

/**
 * Visual required indicator. Hidden from assistive technologies: the requirement is already
 * exposed through the control's `aria-required`, and keeping it out of the `<label>` keeps the
 * accessible name equal to the field label.
 */
const RequiredMark = ({ className }: { className?: string }) => (
  <span aria-hidden="true" className={className}>
    *
  </span>
);

const getFormPages = (formSchema: FormSchema): FormPage[] => {
  const allFieldIds = Object.keys(formSchema.fields);
  const fallbackPage = [{ id: "form", fields: allFieldIds }];
  const pages = Object.entries(formSchema.pages ?? {}).map(([id, page]) => ({
    id,
    ...page,
  }));

  if (!pages.length) return fallbackPage;

  const pageFieldIds = pages.flatMap((page) => page.fields);
  const uniquePageFieldIds = new Set(pageFieldIds);
  const hasInvalidPages = pages.some((page) => !page.id || page.fields.length === 0);
  const hasInvalidFieldIds =
    uniquePageFieldIds.size !== pageFieldIds.length ||
    pageFieldIds.some((fieldId) => !(fieldId in formSchema.fields)) ||
    allFieldIds.some((fieldId) => !uniquePageFieldIds.has(fieldId));

  return hasInvalidPages || hasInvalidFieldIds ? fallbackPage : pages;
};

export type MessageStepFormProps = {
  onSetStepValue: React.Dispatch<React.SetStateAction<StepValues>>;
  formSchema: FormSchema;
  send: (event: AnyEventObject) => void;
  stateDef: FsmDefFormState;
  stateValue: string;
  className: string;
  id: string | undefined;
  /** Called when the form success message should be published (`trigger: onSubmit`). */
  onShowSuccess?: (
    content: FsmDefFormSuccessContent,
    values: Record<string, string | boolean>
  ) => void;
};

export const MessageStepForm = ({
  onSetStepValue,
  formSchema,
  send,
  stateDef,
  stateValue,
  className,
  id,
  onShowSuccess,
}: MessageStepFormProps) => {
  const t = useTranslations("do-chat-v2");
  const accessibilityId = useId();
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pageDirection, setPageDirection] = useState<1 | -1>(1);
  const [pageHeight, setPageHeight] = useState<number | "auto">("auto");
  const [isHeightAnimating, setIsHeightAnimating] = useState(false);
  const [isPageChanging, setIsPageChanging] = useState(false);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const pagePanelRef = useRef<HTMLDivElement>(null);
  const focusAnimationFrameRef = useRef<number | null>(null);
  const heightAnimationFrameRef = useRef<number | null>(null);
  const heightFallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigationLockRef = useRef(false);
  const shouldFocusPageRef = useRef(false);
  const {
    disabled,
    canBackToPreviousStep,
    handleBackToPreviousStep,
    selectedLocale,
    defaultLocale,
    currentLocale,
    chatbotParams,
  } = useChat();
  const motionEnabled = useMotionEnabled();

  // The locale key (e.g. "fr_FR") whose `isDefault` flag is set, used as the fallback
  // when a validation message is missing the current locale.
  const defaultLocaleKey = useMemo(() => {
    const locales = chatbotParams?.availableLocales ?? {};
    return Object.entries(locales).find(([, config]) => config?.isDefault)?.[0] ?? "fr_FR";
  }, [chatbotParams?.availableLocales]);

  const localeKey = currentLocale ?? defaultLocaleKey;
  const formPages = useMemo(() => getFormPages(formSchema), [formSchema]);
  const currentPage = formPages[currentPageIndex] ?? formPages[0];
  const isMultiStep = formPages.length > 1;
  const isFirstPage = currentPageIndex === 0;
  const isLastPage = currentPageIndex === formPages.length - 1;
  const globalHeader = formSchema.header?.[localeKey];
  const pageHeader = currentPage.header?.[localeKey];
  const currentHeader = currentPage.header
    ? pageHeader
      ? { ...globalHeader, ...pageHeader }
      : undefined
    : globalHeader;
  const stepCountId = `${accessibilityId}-step-count`;
  const pageHeadingId = `${accessibilityId}-${currentPage.id}-heading`;
  const stepCountLabel = t("chat.messages.actions.form-step-count", {
    current: currentPageIndex + 1,
    total: formPages.length,
  });
  const currentPageFields = useMemo(
    () =>
      currentPage.fields.flatMap((fieldId) => {
        const field = formSchema.fields[fieldId];
        return field ? ([[fieldId, field]] as const) : [];
      }),
    [currentPage.fields, formSchema.fields]
  );

  // Map each (non-markdown) field's hand-authored validationRules to a Zod schema.
  const formZodSchema = useMemo(() => {
    const shape = Object.entries(formSchema.fields).reduce<Record<string, z.ZodTypeAny>>(
      (acc, [fieldId, field]) => {
        if (field.type === FieldType.markdown) return acc;
        acc[fieldId] = buildZodFromRules(field.validationRules, localeKey, defaultLocaleKey);
        return acc;
      },
      {}
    );
    return z.object(shape);
  }, [formSchema.fields, localeKey, defaultLocaleKey]);

  const defaultValues = useMemo<FormValues>(() => {
    return Object.entries(formSchema.fields).reduce<FormValues>((acc, [fieldId, field]) => {
      if (field.type === FieldType.markdown) return acc;
      acc[fieldId] = field.defaultValue ?? (field.type === FieldType.checkbox ? false : "");
      return acc;
    }, {});
  }, [formSchema.fields]);

  const { control, handleSubmit, getValues, trigger } = useForm<FormValues>({
    resolver: zodResolver(formZodSchema),
    defaultValues,
    mode: "onTouched",
    shouldUnregister: false,
  });

  // Only runs once validation passes. We forward the raw field values (strings /
  // booleans), matching the previous step-value contract: every field present,
  // empty values as "".
  const onValid = useCallback(() => {
    if (!isLastPage) return;

    const values = getValues();
    onSetStepValue((stepValues: StepValues) => ({
      ...stepValues,
      [stateValue]: Object.keys(values).reduce((acc, key) => {
        const value = values[key];
        const fieldValue = typeof value === "boolean" ? value : escapeValue(String(value ?? ""));
        return { ...acc, [key]: fieldValue };
      }, {}),
    }));

    // Publish success as a conversation message before leaving the form state.
    if (getFormSuccessTrigger(formSchema.success) === "onSubmit") {
      const content = resolveFormSuccessContent(formSchema.success, localeKey, defaultLocaleKey);
      if (content) onShowSuccess?.(content, values);
    }

    setTimeout(() => send({ type: Object.keys(stateDef.on)[0] }), 0);
  }, [
    defaultLocaleKey,
    formSchema.success,
    getValues,
    isLastPage,
    localeKey,
    onSetStepValue,
    onShowSuccess,
    send,
    stateDef.on,
    stateValue,
  ]);

  const finishHeightTransition = useCallback(() => {
    if (heightFallbackTimeoutRef.current !== null) {
      clearTimeout(heightFallbackTimeoutRef.current);
      heightFallbackTimeoutRef.current = null;
    }

    setPageHeight("auto");
    setIsHeightAnimating(false);
    setIsPageChanging(false);
    navigationLockRef.current = false;
  }, []);

  const prepareHeightTransition = useCallback(() => {
    const currentHeight = pageContainerRef.current?.getBoundingClientRect().height;
    if (!currentHeight) {
      setIsPageChanging(false);
      navigationLockRef.current = false;
      return;
    }

    if (heightFallbackTimeoutRef.current !== null) {
      clearTimeout(heightFallbackTimeoutRef.current);
    }

    setPageHeight(currentHeight);
    setIsHeightAnimating(true);
    heightFallbackTimeoutRef.current = setTimeout(
      finishHeightTransition,
      (PAGE_TRANSITION_DURATION_SECONDS + PAGE_HEIGHT_TRANSITION_DURATION_SECONDS) * 1000 + 200
    );
  }, [finishHeightTransition]);

  const releaseNavigationLock = useCallback(() => {
    navigationLockRef.current = false;
    setIsPageChanging(false);
  }, []);

  const handleNextPage = useCallback(async () => {
    if (navigationLockRef.current) return;

    navigationLockRef.current = true;
    setIsPageChanging(true);

    try {
      const fieldIds = currentPage.fields.filter(
        (fieldId) => formSchema.fields[fieldId]?.type !== FieldType.markdown
      );
      const isCurrentPageValid =
        fieldIds.length === 0 || (await trigger(fieldIds, { shouldFocus: true }));

      if (!isCurrentPageValid) {
        releaseNavigationLock();
        return;
      }

      prepareHeightTransition();
      shouldFocusPageRef.current = true;
      setPageDirection(1);
      setCurrentPageIndex((pageIndex) => Math.min(pageIndex + 1, formPages.length - 1));
    } catch {
      releaseNavigationLock();
    }
  }, [
    currentPage.fields,
    formPages.length,
    formSchema.fields,
    prepareHeightTransition,
    releaseNavigationLock,
    trigger,
  ]);

  const handlePreviousPage = useCallback(() => {
    if (navigationLockRef.current) return;

    navigationLockRef.current = true;
    setIsPageChanging(true);
    prepareHeightTransition();
    shouldFocusPageRef.current = true;
    setPageDirection(-1);
    setCurrentPageIndex((pageIndex) => Math.max(pageIndex - 1, 0));
  }, [prepareHeightTransition]);

  useEffect(
    () => () => {
      if (heightAnimationFrameRef.current !== null) {
        cancelAnimationFrame(heightAnimationFrameRef.current);
      }
      if (focusAnimationFrameRef.current !== null) {
        cancelAnimationFrame(focusAnimationFrameRef.current);
      }
      if (heightFallbackTimeoutRef.current !== null) {
        clearTimeout(heightFallbackTimeoutRef.current);
      }
    },
    []
  );

  const handleFormSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      if (!isLastPage) {
        event.preventDefault();
        void handleNextPage();
        return;
      }

      void handleSubmit(onValid)(event);
    },
    [handleNextPage, handleSubmit, isLastPage, onValid]
  );

  return (
    <div
      className={cn(
        className,
        "relative z-2 overflow-hidden",
        "rounded-(--message-steps-form-border-radius)",
        "border-(length:--message-steps-form-border-width)",
        "border-(--message-steps-form-border-color)/(--message-steps-form-border-opacity)",
        "bg-(--message-steps-form-background)/(--message-steps-form-background-opacity)"
      )}
      id={id}
    >
      {isMultiStep && (
        <>
          <Progress
            aria-labelledby={stepCountId}
            aria-valuetext={stepCountLabel}
            value={((currentPageIndex + 1) / formPages.length) * 100}
            className="rounded-none"
          />

          <p
            id={stepCountId}
            aria-atomic="true"
            aria-live="polite"
            role="status"
            className={cn(
              "italic",
              "text-(length:--message-steps-form-step-count-font-size)",
              "font-(--message-steps-form-step-count-font-weight)!",
              "font-(family-name:--message-steps-form-step-count-font-family)",
              "text-(--message-steps-form-step-count-color)/(--message-steps-form-step-count-color-opacity)",
              "p-(--message-steps-form-padding) pb-0",
              (currentHeader?.title || currentHeader?.subtitle) &&
                "mb-[calc(var(--message-steps-form-padding)*-1+var(--message-steps-form-step-count-margin-bottom))]"
            )}
          >
            {stepCountLabel}
          </p>
        </>
      )}

      <form
        id="chat-form"
        onSubmit={handleFormSubmit}
        className={cn("flex flex-col gap-4", "p-(--message-steps-form-padding)")}
        noValidate
      >
        <motion.div
          animate={{ height: pageHeight }}
          initial={false}
          onAnimationComplete={() => {
            if (pageHeight === "auto") finishHeightTransition();
          }}
          ref={pageContainerRef}
          style={{ overflow: isHeightAnimating ? "hidden" : "visible" }}
          transition={
            motionEnabled
              ? {
                  height: {
                    duration: PAGE_HEIGHT_TRANSITION_DURATION_SECONDS,
                    ease: [0.22, 1, 0.36, 1],
                  },
                }
              : INSTANT_TRANSITION
          }
        >
          <AnimatePresence custom={pageDirection} initial={false} mode="wait">
            <motion.div
              animate="center"
              className="flex flex-col gap-4"
              custom={pageDirection}
              exit="exit"
              initial="enter"
              key={currentPage.id}
              aria-hidden={isPageChanging || undefined}
              aria-labelledby={currentHeader?.title ? pageHeadingId : stepCountId}
              inert={isPageChanging || undefined}
              onAnimationComplete={(definition) => {
                if (definition !== "center") return;

                setIsPageChanging(false);
                navigationLockRef.current = false;
                if (shouldFocusPageRef.current) {
                  shouldFocusPageRef.current = false;
                  focusAnimationFrameRef.current = requestAnimationFrame(() => {
                    pagePanelRef.current?.focus({ preventScroll: true });
                    focusAnimationFrameRef.current = null;
                  });
                }
              }}
              onAnimationStart={(definition) => {
                if (definition !== "center" || typeof pageHeight !== "number") return;

                if (heightAnimationFrameRef.current !== null) {
                  cancelAnimationFrame(heightAnimationFrameRef.current);
                }

                heightAnimationFrameRef.current = requestAnimationFrame(() => {
                  setPageHeight("auto");
                  heightAnimationFrameRef.current = null;
                });
              }}
              ref={pagePanelRef}
              role="group"
              tabIndex={-1}
              // Instant rather than skipped when motion is off: the page still has to reach
              // "center", which unlocks the navigation and moves the focus.
              transition={
                motionEnabled
                  ? { duration: PAGE_TRANSITION_DURATION_SECONDS, ease: "easeOut" }
                  : INSTANT_TRANSITION
              }
              variants={{
                enter: (direction: 1 | -1) => ({
                  opacity: 0,
                  x: direction > 0 ? 80 : -80,
                }),
                center: { opacity: 1, x: 0 },
                exit: (direction: 1 | -1) => ({
                  opacity: 0,
                  x: direction > 0 ? -80 : 80,
                }),
              }}
            >
              {(currentHeader?.title || currentHeader?.subtitle) && (
                <div className="flex flex-col">
                  {currentHeader?.title && (
                    <h3
                      id={pageHeadingId}
                      className={cn(
                        "text-(length:--message-steps-form-title-font-size)",
                        "font-(--message-steps-form-title-font-weight)!",
                        "font-(family-name:--message-steps-form-title-font-family)",
                        "text-(--message-steps-form-title-color)/(--message-steps-form-title-color-opacity)"
                      )}
                    >
                      {currentHeader.title}
                    </h3>
                  )}
                  {currentHeader?.subtitle && (
                    <p
                      className={cn(
                        "text-(length:--message-steps-form-sub-title-font-size)",
                        "font-(--message-steps-form-sub-title-font-weight)!",
                        "font-(family-name:--message-steps-form-sub-title-font-family)",
                        "text-(--message-steps-form-sub-title-color)/(--message-steps-form-sub-title-color-opacity)"
                      )}
                    >
                      {currentHeader.subtitle}
                    </p>
                  )}
                </div>
              )}

              {currentPageFields.map(([id, field]) => {
                if (field.type === FieldType.markdown) {
                  return <MarkdownRenderer key={`md-${id}`} text={field.props.text} />;
                }

                const descriptionId = `${accessibilityId}-${id}-description`;
                const errorId = `${accessibilityId}-${id}-error`;
                const labelId = `${accessibilityId}-${id}-label`;
                const tooltipId = `${accessibilityId}-${id}-tooltip`;
                // Descriptions and tooltips are always announced with the field; the error message
                // is appended only while the field is invalid.
                const staticDescribedByIds = [
                  field.props.description ? descriptionId : undefined,
                  field.props.tooltip ? tooltipId : undefined,
                ].filter((describedById): describedById is string => Boolean(describedById));
                const resolveDescribedBy = (isInvalid: boolean) => {
                  const describedByIds = isInvalid
                    ? [...staticDescribedByIds, errorId]
                    : staticDescribedByIds;
                  return describedByIds.length ? describedByIds.join(" ") : undefined;
                };

                return (
                  <div key={id} className="w-full flex flex-col gap-2">
                    {field.props.label && field.type !== FieldType.checkbox && (
                      <div className="flex items-center gap-2">
                        <div className="flex items-baseline gap-1">
                          <Label
                            htmlFor={field.type === FieldType.select ? `${id}-select-trigger` : id}
                            id={labelId}
                            className={labelClassName}
                          >
                            {field.props.label}
                          </Label>

                          {isFieldRequired(field) && <RequiredMark className={labelClassName} />}
                        </div>

                        {field.props.tooltip && (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InfoIcon
                                  aria-hidden="true"
                                  className={cn(
                                    "pb-px",
                                    "size-(--message-steps-form-tooltip-icon-size)",
                                    "text-(--message-steps-form-tooltip-icon-color)/(--message-steps-form-tooltip-icon-color-opacity)"
                                  )}
                                />
                              </TooltipTrigger>
                              <TooltipContent
                                className={cn(
                                  "bg-(--message-steps-form-tooltip-content-background)/(--message-steps-form-tooltip-content-background-opacity)",
                                  "text-(--message-steps-form-tooltip-content-foreground)/(--message-steps-form-tooltip-content-foreground-opacity)",
                                  "text-(length:--message-steps-form-tooltip-content-font-size)",
                                  "font-(--message-steps-form-tooltip-content-font-weight)!",
                                  "font-(family-name:--message-steps-form-tooltip-content-font-family)",
                                  "rounded-(--message-steps-form-tooltip-content-border-radius)",
                                  "z-99999"
                                )}
                              >
                                {field.props.tooltip}
                              </TooltipContent>
                            </Tooltip>

                            <span id={tooltipId} className="sr-only">
                              {field.props.tooltip}
                            </span>
                          </>
                        )}
                      </div>
                    )}

                    <Controller
                      control={control}
                      name={id}
                      render={({ field: rhfField, fieldState }) => (
                        <Field data-invalid={fieldState.invalid} className="w-full">
                          {(() => {
                            if (
                              (field.type === FieldType.textfield && field.props.multiline) ||
                              field.type === FieldType.textarea
                            ) {
                              const props = field.props as
                                | FieldTextFieldTextareaProps
                                | FieldTextArea["props"];
                              const {
                                className: fieldClassName,
                                description: _description,
                                label,
                                minRows,
                                multiline: _multiline,
                                tooltip: _tooltip,
                                ...textareaProps
                              } = props as FieldTextFieldTextareaProps;
                              return (
                                <div key={`textarea-${id}`} className="w-full">
                                  <Textarea
                                    {...textareaProps}
                                    className={cn(
                                      "resize-none field-sizing-fixed",
                                      "min-h-(--message-steps-form-input-height)",
                                      "md:text-(length:--message-steps-form-input-font-size)!",
                                      "placeholder:text-(--message-steps-form-input-placeholder-color)/(--message-steps-form-input-placeholder-color-opacity)",
                                      "rounded-(--message-steps-form-input-border-radius)",
                                      "border-(--message-steps-form-input-border-color)/(--message-steps-form-input-border-opacity)",
                                      "bg-(--message-steps-form-input-background-color)/(--message-steps-form-input-background-opacity)",
                                      "text-(--message-steps-form-input-foreground)/(--message-steps-form-input-foreground-opacity)",
                                      "focus-visible:border-(--message-steps-form-input-focus-border-color)/(--message-steps-form-input-focus-border-opacity)",
                                      "focus-visible:bg-(--message-steps-form-input-focus-background-color)/(--message-steps-form-input-focus-background-opacity)",
                                      "focus-visible:text-(--message-steps-form-input-focus-foreground)/(--message-steps-form-input-focus-foreground-opacity)",
                                      "focus-visible:ring-(length:--message-steps-form-input-focus-ring-width)",
                                      "focus-visible:ring-(--message-steps-form-input-focus-ring-color)/(--message-steps-form-input-focus-ring-opacity)",
                                      "focus-visible:ring-offset-(length:--message-steps-form-input-focus-ring-offset-width)",
                                      "px-(--message-steps-form-input-padding-x)",
                                      "py-(--message-steps-form-input-padding-y)",
                                      "leading-(--message-steps-form-input-line-height)",
                                      "[box-shadow:var(--message-steps-form-input-box-shadow)]",
                                      "font-(family-name:--message-steps-form-input-font-family)",
                                      fieldClassName
                                    )}
                                    id={id}
                                    disabled={disabled}
                                    rows={textareaProps.rows ?? minRows ?? 3}
                                    name={rhfField.name}
                                    ref={rhfField.ref}
                                    value={(rhfField.value as string) ?? ""}
                                    placeholder={textareaProps.placeholder}
                                    aria-labelledby={label ? labelId : undefined}
                                    aria-label={label ? undefined : textareaProps.placeholder}
                                    aria-describedby={resolveDescribedBy(fieldState.invalid)}
                                    aria-errormessage={fieldState.invalid ? errorId : undefined}
                                    aria-required={isFieldRequired(field)}
                                    aria-invalid={fieldState.invalid}
                                    required={isFieldRequired(field)}
                                    onChange={rhfField.onChange}
                                    onBlur={rhfField.onBlur}
                                  />
                                </div>
                              );
                            }

                            if (
                              field.type === FieldType.textfield ||
                              field.type === FieldType.input
                            ) {
                              const props = field.props as
                                | FieldTextFieldInputProps
                                | FieldInput["props"];
                              const {
                                className: fieldClassName,
                                description: _description,
                                inputProps,
                                label,
                                multiline: _multiline,
                                tooltip: _tooltip,
                                ...fieldInputProps
                              } = props as FieldTextFieldInputProps;
                              return (
                                <div key={`textfield-${id}`} className="w-full">
                                  <Input
                                    {...fieldInputProps}
                                    {...inputProps}
                                    className={cn(
                                      "h-(--message-steps-form-input-height)",
                                      "md:text-(length:--message-steps-form-input-font-size)!",
                                      "placeholder:text-(--message-steps-form-input-placeholder-color)/(--message-steps-form-input-placeholder-color-opacity)",
                                      "rounded-(--message-steps-form-input-border-radius)",
                                      "border-(--message-steps-form-input-border-color)/(--message-steps-form-input-border-opacity)",
                                      "bg-(--message-steps-form-input-background-color)/(--message-steps-form-input-background-opacity)",
                                      "text-(--message-steps-form-input-foreground)/(--message-steps-form-input-foreground-opacity)",
                                      "focus-visible:border-(--message-steps-form-input-focus-border-color)/(--message-steps-form-input-focus-border-opacity)",
                                      "focus-visible:bg-(--message-steps-form-input-focus-background-color)/(--message-steps-form-input-focus-background-opacity)",
                                      "focus-visible:text-(--message-steps-form-input-focus-foreground)/(--message-steps-form-input-focus-foreground-opacity)",
                                      "focus-visible:ring-(length:--message-steps-form-input-focus-ring-width)",
                                      "focus-visible:ring-(--message-steps-form-input-focus-ring-color)/(--message-steps-form-input-focus-ring-opacity)",
                                      "focus-visible:ring-offset-(length:--message-steps-form-input-focus-ring-offset-width)",
                                      "px-(--message-steps-form-input-padding-x)",
                                      "py-(--message-steps-form-input-padding-y)",
                                      "leading-(--message-steps-form-input-line-height)",
                                      "[box-shadow:var(--message-steps-form-input-box-shadow)]",
                                      "font-(family-name:--message-steps-form-input-font-family)",
                                      fieldClassName,
                                      inputProps?.className
                                    )}
                                    id={id}
                                    disabled={disabled}
                                    name={rhfField.name}
                                    ref={rhfField.ref}
                                    value={(rhfField.value as string) ?? ""}
                                    placeholder={
                                      inputProps?.placeholder ?? fieldInputProps.placeholder
                                    }
                                    aria-labelledby={label ? labelId : undefined}
                                    aria-label={
                                      label
                                        ? undefined
                                        : inputProps?.placeholder ?? fieldInputProps.placeholder
                                    }
                                    aria-describedby={resolveDescribedBy(fieldState.invalid)}
                                    aria-errormessage={fieldState.invalid ? errorId : undefined}
                                    aria-required={isFieldRequired(field)}
                                    aria-invalid={fieldState.invalid}
                                    required={isFieldRequired(field)}
                                    onChange={rhfField.onChange}
                                    onBlur={rhfField.onBlur}
                                  />
                                </div>
                              );
                            }

                            if (field.type === FieldType.select) {
                              const { props } = field;
                              const {
                                className: fieldClassName,
                                description: _description,
                                label,
                                options,
                                placeholder,
                                required: _required,
                                tooltip: _tooltip,
                                ...selectTriggerProps
                              } = props;
                              return (
                                <div key={`select-${id}`} className="w-full">
                                  <Select
                                    disabled={disabled}
                                    onValueChange={rhfField.onChange}
                                    value={(rhfField.value as string) || undefined}
                                  >
                                    <SelectTrigger
                                      {...selectTriggerProps}
                                      id={`${id}-select-trigger`}
                                      aria-describedby={resolveDescribedBy(fieldState.invalid)}
                                      aria-errormessage={fieldState.invalid ? errorId : undefined}
                                      aria-labelledby={label ? labelId : undefined}
                                      aria-label={label ? undefined : placeholder}
                                      aria-invalid={fieldState.invalid}
                                      ref={rhfField.ref}
                                      onBlur={rhfField.onBlur}
                                      size="none"
                                      className={cn(
                                        "w-full",
                                        "h-(--message-steps-form-select-height)",
                                        "md:text-(length:--message-steps-form-select-font-size)!",
                                        "placeholder:text-(--message-steps-form-select-placeholder-color)/(--message-steps-form-select-placeholder-color-opacity)",
                                        "rounded-(--message-steps-form-select-border-radius)",
                                        "border-(--message-steps-form-select-border-color)/(--message-steps-form-select-border-opacity)",
                                        "bg-(--message-steps-form-select-background-color)/(--message-steps-form-select-background-opacity)",
                                        "text-(--message-steps-form-select-foreground)/(--message-steps-form-select-foreground-opacity)",
                                        "focus-visible:border-(--message-steps-form-select-focus-border-color)/(--message-steps-form-select-focus-border-opacity)",
                                        "focus-visible:bg-(--message-steps-form-select-focus-background-color)/(--message-steps-form-select-focus-background-opacity)",
                                        "focus-visible:text-(--message-steps-form-select-focus-foreground)/(--message-steps-form-select-focus-foreground-opacity)",
                                        "focus-visible:ring-(length:--message-steps-form-select-focus-ring-width)",
                                        "focus-visible:ring-(--message-steps-form-select-focus-ring-color)/(--message-steps-form-select-focus-ring-opacity)",
                                        "focus-visible:ring-offset-(length:--message-steps-form-select-focus-ring-offset-width)",
                                        "px-(--message-steps-form-select-padding-x)",
                                        "py-(--message-steps-form-select-padding-y)",
                                        "leading-(--message-steps-form-select-line-height)",
                                        "data-[placeholder]:text-(--message-steps-form-select-placeholder-color)/(--message-steps-form-select-placeholder-color-opacity)",
                                        "[box-shadow:var(--message-steps-form-select-box-shadow)]",
                                        "font-(family-name:--message-steps-form-select-font-family)",
                                        fieldClassName
                                      )}
                                      aria-required={isFieldRequired(field)}
                                    >
                                      <SelectValue placeholder={placeholder ?? label} />
                                    </SelectTrigger>
                                    <SelectContent id={`${id}-select-content`}>
                                      {options?.map((option) => (
                                        <SelectItem
                                          key={option.value}
                                          value={option.value}
                                          id={`${id}-select-item-${option.value}`}
                                        >
                                          {option.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              );
                            }

                            if (field.type === FieldType.checkbox) {
                              const { props } = field;
                              const {
                                description: _description,
                                label,
                                required: _required,
                                tooltip,
                                ...checkboxProps
                              } = props;
                              return (
                                <div className="flex items-center">
                                  <Checkbox
                                    {...checkboxProps}
                                    key={`checkbox-${id}`}
                                    id={`${id}-checkbox`}
                                    disabled={disabled}
                                    ref={rhfField.ref}
                                    onBlur={rhfField.onBlur}
                                    aria-labelledby={label ? labelId : undefined}
                                    aria-describedby={resolveDescribedBy(fieldState.invalid)}
                                    aria-errormessage={fieldState.invalid ? errorId : undefined}
                                    aria-invalid={fieldState.invalid}
                                    checked={Boolean(rhfField.value)}
                                    onCheckedChange={(checked: boolean) =>
                                      rhfField.onChange(checked)
                                    }
                                    className={cn(
                                      "size-(--message-steps-form-checkbox-size)",
                                      "rounded-(--message-steps-form-checkbox-border-radius)",
                                      "border-(length:--message-steps-form-checkbox-border-width)",
                                      "border-(--message-steps-form-checkbox-border-color)/(--message-steps-form-checkbox-border-opacity)",
                                      "bg-(--message-steps-form-checkbox-background-color)/(--message-steps-form-checkbox-background-opacity)",
                                      "text-(--message-steps-form-checkbox-icon-color)/(--message-steps-form-checkbox-icon-color-opacity)",
                                      "data-[state=checked]:bg-(--message-steps-form-checkbox-checked-background-color)/(--message-steps-form-checkbox-checked-background-opacity)",
                                      "data-[state=checked]:text-(--message-steps-form-checkbox-checked-icon-color)/(--message-steps-form-checkbox-checked-icon-color-opacity)",
                                      "data-[state=checked]:border-(--message-steps-form-checkbox-checked-border-color)/(--message-steps-form-checkbox-checked-border-opacity)",
                                      "[box-shadow:var(--message-steps-form-checkbox-box-shadow)]"
                                    )}
                                    iconClassName="size-(--message-steps-form-checkbox-icon-size) stroke-(length:--message-steps-form-checkbox-icon-stroke-width)"
                                    required={isFieldRequired(field)}
                                    aria-required={isFieldRequired(field)}
                                  />
                                  <div className="flex items-center gap-2">
                                    <div className="flex items-baseline gap-1">
                                      <Label
                                        id={labelId}
                                        htmlFor={`${id}-checkbox`}
                                        className={cn(
                                          "pl-(--message-steps-form-checkbox-gap)",
                                          "text-(--message-steps-form-checkbox-label-color)/(--message-steps-form-checkbox-label-color-opacity)",
                                          "font-(--message-steps-form-checkbox-label-font-weight)! font-(family-name:--message-steps-form-checkbox-label-font-family)",
                                          "text-(length:--message-steps-form-checkbox-label-font-size)",
                                          "leading-(--message-steps-form-checkbox-label-line-height)"
                                        )}
                                      >
                                        {label}
                                      </Label>

                                      {isFieldRequired(field) && (
                                        <RequiredMark
                                          className={cn(
                                            "text-(--message-steps-form-checkbox-label-color)/(--message-steps-form-checkbox-label-color-opacity)",
                                            "text-(length:--message-steps-form-checkbox-label-font-size)"
                                          )}
                                        />
                                      )}
                                    </div>

                                    {tooltip && (
                                      <>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <InfoIcon
                                              aria-hidden="true"
                                              className="size-3.5 text-muted-foreground pb-px"
                                            />
                                          </TooltipTrigger>
                                          <TooltipContent>{tooltip}</TooltipContent>
                                        </Tooltip>

                                        <span id={tooltipId} className="sr-only">
                                          {tooltip}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            }

                            return null;
                          })()}
                          {fieldState.invalid && (
                            <FieldError id={errorId} errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />

                    {field.props.description && (
                      <p
                        id={descriptionId}
                        className={cn(
                          "leading-[1]!",
                          "text-(--message-steps-form-description-color)/(--message-steps-form-description-color-opacity)",
                          "text-(length:--message-steps-form-description-font-size)",
                          "font-(--message-steps-form-description-font-weight)!",
                          "font-(family-name:--message-steps-form-description-font-family)"
                        )}
                      >
                        {field.props.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </motion.div>

        <div
          id="chat-form-submit-container"
          className="flex sm:justify-end justify-center flex-wrap gap-2"
        >
          {(!isFirstPage || canBackToPreviousStep) && (
            <Button
              id="chat-form-back-button"
              className={cn(
                "group/message-button max-sm:flex-1",
                "bg-(--message-steps-form-back-button-background)/(--message-steps-form-back-button-background-opacity)",
                "hover:bg-(--message-steps-form-back-button-hover-background)/(--message-steps-form-back-button-hover-background-opacity)",
                "text-(--message-steps-form-back-button-foreground)/(--message-steps-form-back-button-foreground-opacity)",
                "hover:text-(--message-steps-form-back-button-hover-foreground)/(--message-steps-form-back-button-hover-foreground-opacity)",
                "border-(length:--message-steps-form-back-button-border-width)",
                "border-(--message-steps-form-back-button-border-color)/(--message-steps-form-back-button-border-opacity)",
                "hover:border-(--message-steps-form-back-button-hover-border-color)/(--message-steps-form-back-button-hover-border-opacity)",
                "rounded-(--message-steps-form-back-button-border-radius)",
                "h-(--message-steps-form-back-button-height)",
                "font-(--message-steps-form-back-button-font-weight)! font-(family-name:--message-steps-form-back-button-font-family)",
                "text-(length:--message-steps-form-back-button-font-size)",
                "[box-shadow:var(--message-steps-form-back-button-box-shadow)]"
              )}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (isFirstPage) {
                  handleBackToPreviousStep();
                } else {
                  handlePreviousPage();
                }
              }}
              disabled={disabled || isPageChanging}
            >
              <ButtonIcon
                template={{}}
                isBackButton
                buttonId="message-steps-form-back-button-icon"
              />
              {isFirstPage
                ? selectedLocale?.backButtonLabel ||
                  defaultLocale?.backButtonLabel ||
                  t("chat.messages.actions.back-to-previous-step")
                : t("chat.messages.actions.form-previous")}
            </Button>
          )}

          <Button
            id={isLastPage ? "chat-form-submit-button" : "chat-form-next-button"}
            className={cn(
              "max-sm:flex-1",
              "bg-(--message-steps-form-send-button-background)/(--message-steps-form-send-button-background-opacity)",
              "hover:bg-(--message-steps-form-send-button-hover-background)/(--message-steps-form-send-button-hover-background-opacity)",
              "text-(--message-steps-form-send-button-foreground)/(--message-steps-form-send-button-foreground-opacity)",
              "hover:text-(--message-steps-form-send-button-hover-foreground)/(--message-steps-form-send-button-hover-foreground-opacity)",
              "border-(length:--message-steps-form-send-button-border-width)",
              "border-(--message-steps-form-send-button-border-color)/(--message-steps-form-send-button-border-opacity)",
              "hover:border-(--message-steps-form-send-button-hover-border-color)/(--message-steps-form-send-button-hover-border-opacity)",
              "rounded-(--message-steps-form-send-button-border-radius)",
              "h-(--message-steps-form-send-button-height)",
              "font-(--message-steps-form-send-button-font-weight)! font-(family-name:--message-steps-form-send-button-font-family)",
              "text-(length:--message-steps-form-send-button-font-size)",
              "[box-shadow:var(--message-steps-form-send-button-box-shadow)]"
            )}
            type={isLastPage ? "submit" : "button"}
            onClick={
              isLastPage
                ? undefined
                : (event) => {
                    event.preventDefault();
                    void handleNextPage();
                  }
            }
            disabled={disabled || isPageChanging}
          >
            {isLastPage ? formSchema.submit : t("chat.messages.actions.form-next")}
          </Button>
        </div>
      </form>
    </div>
  );
};
